import React from "react";

/**
 * PremiumLeaveCard
 *
 * Props:
 *   title    – leave type label
 *   allowed  – total / allocated days
 *   used     – days already consumed
 *   color    – theme key: casual | sick | compensatory | taken | total | balance | default
 *   simple   – (legacy, kept for compat) if true renders compact display
 *
 * Display format:
 *   Big number = Used / Allocated   e.g.  1 / 8
 *   Footer     = Used: 1   Available: 7
 *
 * No "% used" badge is shown.
 */
const PremiumLeaveCard = ({ title, allowed = 0, used = 0, color, value, simple }) => {
  // ── colour themes ──────────────────────────────────────────────
  const colorMap = {
    casual: {
      bg: "bg-gradient-to-br from-blue-50 to-indigo-50/50",
      border: "border-blue-100 hover:border-blue-300",
      bar: "bg-gradient-to-r from-blue-500 to-indigo-600",
      badgeBg: "bg-blue-100/80 text-blue-800",
      glow: "hover:shadow-blue-100/50",
    },
    sick: {
      bg: "bg-gradient-to-br from-emerald-50 to-teal-50/50",
      border: "border-emerald-100 hover:border-emerald-300",
      bar: "bg-gradient-to-r from-emerald-500 to-teal-600",
      badgeBg: "bg-emerald-100/80 text-emerald-800",
      glow: "hover:shadow-emerald-100/50",
    },
    compensatory: {
      bg: "bg-gradient-to-br from-amber-50 to-orange-50/50",
      border: "border-amber-100 hover:border-amber-300",
      bar: "bg-gradient-to-r from-amber-500 to-orange-600",
      badgeBg: "bg-amber-100/80 text-amber-800",
      glow: "hover:shadow-amber-100/50",
    },
    total: {
      bg: "bg-gradient-to-br from-sky-50 to-cyan-50/50",
      border: "border-sky-100 hover:border-sky-300",
      bar: "bg-gradient-to-r from-sky-500 to-cyan-600",
      badgeBg: "bg-sky-100/80 text-sky-800",
      glow: "hover:shadow-sky-100/50",
    },
    taken: {
      bg: "bg-gradient-to-br from-rose-50 to-pink-50/50",
      border: "border-rose-100 hover:border-rose-300",
      bar: "bg-gradient-to-r from-rose-500 to-pink-600",
      badgeBg: "bg-rose-100/80 text-rose-800",
      glow: "hover:shadow-rose-100/50",
    },
    balance: {
      bg: "bg-gradient-to-br from-violet-50 to-purple-50/50",
      border: "border-violet-100 hover:border-violet-300",
      bar: "bg-gradient-to-r from-violet-500 to-purple-600",
      badgeBg: "bg-violet-100/80 text-violet-800",
      glow: "hover:shadow-violet-100/50",
    },
    default: {
      bg: "bg-gradient-to-br from-slate-50 to-zinc-50/50",
      border: "border-slate-100 hover:border-slate-300",
      bar: "bg-gradient-to-r from-slate-500 to-zinc-600",
      badgeBg: "bg-slate-100/80 text-slate-800",
      glow: "hover:shadow-slate-100/50",
    },
  };

  const theme = colorMap[color] || colorMap.default;
  const baseClass = `relative overflow-hidden p-6 rounded-2xl border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${theme.bg} ${theme.border} ${theme.glow}`;

  // ── derived values ─────────────────────────────────────────────
  // Support both plain number values (summary cards) and leave-type cards
  const numAllowed  = Number(allowed ?? 0);
  const numUsed     = Number(used ?? 0);
  const available   = Math.max(numAllowed - numUsed, 0);
  const hasDetail   = numAllowed > 0 || numUsed > 0;

  // ── simple mode — same look, just shows a single value (e.g. Total/Taken/Balance) ──
  if (simple && value !== undefined) {
    return (
      <div className={baseClass}>
        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${theme.badgeBg}`}>
          {title}
        </span>
        <p className="text-4xl font-extrabold text-gray-900 tracking-tight mt-5">
          {String(value)}
        </p>
      </div>
    );
  }

  // ── Full leave-type card — "Used / Allocated" format ──
  return (
    <div className={baseClass}>
      {/* Decorative circle */}
      <div className="absolute right-0 top-0 -mt-4 -mr-4 w-20 h-20 rounded-full opacity-10 bg-current pointer-events-none" />

      {/* Title badge — NO "% used" */}
      <div className="mb-4">
        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${theme.badgeBg}`}>
          {title}
        </span>
      </div>

      {/* Big number: Used / Allocated */}
      <div className="flex items-baseline gap-1 mt-2">
        <span className="text-3xl font-extrabold text-gray-900 tracking-tight">
          {numUsed}
        </span>
        {hasDetail && (
          <span className="text-sm font-medium text-gray-400">/ {numAllowed}</span>
        )}
      </div>

      {/* Progress bar + footer */}
      {hasDetail ? (
        <div className="mt-4">
          <div className="w-full bg-gray-200/70 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${theme.bar}`}
              style={{ width: numAllowed > 0 ? `${Math.min((numUsed / numAllowed) * 100, 100)}%` : "0%" }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1.5 font-medium">
            <span>Used: {numUsed}</span>
            <span>Available: {available}</span>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400 mt-3 font-medium">No balance configured</p>
      )}
    </div>
  );
};

export default PremiumLeaveCard;
